import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { FichaClinicaStore } from '../../../../shared/stores/ficha-clinica/ficha-clinica.store';
import { Subscription } from 'rxjs';
import { fichaClinicaFinalizada } from '../../../../shared/helpers/ficha-clinica.helper';
import { MatDialog } from '@angular/material/dialog';
import {
  ModalConfirmComponent,
  ModalConfirmProps,
  ModalConfirmType,
} from '../../../../shared/components/modal-confirm/modal-confirm.component';
import { FichaClinicaService } from '../../../services/ficha-clinica.service';
import { EstadoFichaClinica } from '../../../../shared/enums';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  SnackInfoComponent,
  SnackInfoProps,
  SnackInfoType,
} from '../../../../shared/components/snack-info/snack-info.component';
import { ResumenClinicoService } from '../../../services/reportes/resument-clinico/resumen-clinico.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'soph-finish-appointment-button',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './finish-appointment-button.component.html',
  styleUrl: './finish-appointment-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinishAppointmentButtonComponent implements OnInit, OnDestroy {
  private readonly _fichaClinicaStore = inject(FichaClinicaStore);
  private readonly _fichaClinicaService = inject(FichaClinicaService);
  private readonly resumenClinico = inject(ResumenClinicoService);

  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly subscriptions: Array<Subscription> = [];

  protected readonly isAllTabsCompleted = signal(false);
  protected readonly isFinishingAppointment = signal(false);

  protected readonly hasUnsavedChanges = signal<boolean>(false);

  ngOnInit(): void {
    const fichaClinicaSubscription = this._fichaClinicaStore.state$.subscribe((values) => {
      this.isAllTabsCompleted.set(
        Object.values(values.historial).every((value) => value === true) &&
          !fichaClinicaFinalizada(values.estado || '')
      );
    });

    const unsavedChangesSubscription = this._fichaClinicaStore.state$.subscribe((values) => {
      this.hasUnsavedChanges.set(values.hasUnsavedChanges);
    });

    this.subscriptions.push(fichaClinicaSubscription, unsavedChangesSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  protected onFinishAppointmentConfirmation() {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      autoFocus: false,
      panelClass: 'custom-confirmation-dialog',
      width: '550px',
      disableClose: true,
      data: {
        type: ModalConfirmType.WARNING,
        description: '¿Confirmas que deseas finalizar la consulta?',
        message:
          'Los datos ingresados se guardarán y solo algunos datos específicos podrán ser editados posteriormente.',
        config: {
          buttons: [
            { title: 'Volver', action: false },
            { title: 'Finalizar consulta', action: true },
          ],
        },
      } as ModalConfirmProps,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (typeof result === 'boolean' && result) {
        this.onFinishAppointment();
      }
    });
  }

  private onFinishAppointment() {
    this.isFinishingAppointment.set(true);

    this._fichaClinicaService.finish(this._fichaClinicaStore.values.idAgendamiento!).subscribe({
      next: () => {
        this._fichaClinicaStore.updateStore({
          ...this._fichaClinicaStore.values,
          estado: EstadoFichaClinica.REALIZADA,
        });

        this.isFinishingAppointment.set(false);

        this.onFinishAppointmentFinished();
      },
      error: () => {
        this.isFinishingAppointment.set(false);

        this.snackBar.openFromComponent(SnackInfoComponent, {
          data: {
            message: 'No pudimos guardar los cambios, por favor intenta nuevamente.',
            type: SnackInfoType.ERROR,
          } as SnackInfoProps,
        });
      },
    });
  }

  private onFinishAppointmentFinished() {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      autoFocus: false,
      panelClass: 'custom-confirmation-dialog',
      width: '452px',
      disableClose: true,
      data: {
        type: ModalConfirmType.SUCCESS,
        description: 'La consulta ha sido finalizada y guardada con éxito',
        message:
          'La ficha clínica de esta consulta está lista para ser revisada cuando lo necesites',
        config: {
          buttons: [{ title: 'Aceptar', action: true }],
        },
      } as ModalConfirmProps,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (typeof result === 'boolean' && result) {
        this.resumenClinico.openResumenClinicoModal(this._fichaClinicaStore.values.idAgendamiento!);
      }
    });
  }
}
